import {
  FeatherAlertCircle,
  FeatherArchive,
  FeatherBan,
  FeatherCheckCircle,
  FeatherClock,
  FeatherXCircle,
} from '@subframe/core';

import { TIPOS_ENCERRAMENTO_COM_ACORDO } from '../constants/tiposEncerramento';
import type { EncerramentoConfig, TipoEncerramento } from '../types/encerramentoTypes';
import type { NegociacaoCasoStatus } from '../types/negociacoesTypes';

// `tipo_encerramento` é coluna livre no banco (sem CHECK) e produção carrega valores
// anteriores à padronização deste enum (ex.: 'ACORDO', 'SEM_ACORDO', string vazia — caso
// 59f30ca8-c311-41e2-8238-27324f7fb735 e outros). `configs[tipo]` some para esses valores;
// sem fallback, `NegociacaoResumoTab`/`ChatBlockedBanner` quebram lendo `.icon` de `undefined`.
const FALLBACK_ENCERRAMENTO_CONFIG: EncerramentoConfig = {
  title: 'Negociação encerrada',
  subtitle: 'Esta negociação foi finalizada.',
  icon: FeatherArchive,
  bgClass: 'bg-neutral-50',
  iconBgClass: 'bg-neutral-100',
  borderClass: 'border-neutral-200',
  textClass: 'text-neutral-700',
  badgeVariant: 'neutral',
};

export const getTipoEncerramentoConfig = (tipo: string | null | undefined): EncerramentoConfig => {
  const configs: Record<string, EncerramentoConfig> = {
    ACORDO_MANUAL: {
      title: 'Acordo fechado!',
      subtitle: 'A negociação foi concluída com sucesso.',
      icon: FeatherCheckCircle,
      bgClass: 'bg-success-50',
      iconBgClass: 'bg-success-100',
      borderClass: 'border-success-200',
      textClass: 'text-success-700',
      badgeVariant: 'success',
    },
    ACORDO_IA: {
      title: 'Acordo fechado!',
      subtitle: 'A negociação foi concluída com sucesso.',
      icon: FeatherCheckCircle,
      bgClass: 'bg-success-50',
      iconBgClass: 'bg-success-100',
      borderClass: 'border-success-200',
      textClass: 'text-success-700',
      badgeVariant: 'success',
    },
    DESISTENCIA: {
      title: 'Negociação interrompida',
      subtitle: 'A contraparte desistiu antes de um acordo ser fechado.',
      icon: FeatherBan,
      bgClass: 'bg-orange-50',
      iconBgClass: 'bg-orange-100',
      borderClass: 'border-orange-200',
      textClass: 'text-orange-700',
      badgeVariant: 'neutral',
    },
    TIMEOUT: {
      title: 'Negociação interrompida',
      subtitle: 'A negociação foi interrompida por falta de resposta da contraparte.',
      icon: FeatherClock,
      bgClass: 'bg-warning-50',
      iconBgClass: 'bg-warning-100',
      borderClass: 'border-warning-200',
      textClass: 'text-warning-700',
      badgeVariant: 'neutral',
    },
    CANCELAMENTO: {
      title: 'Negociação interrompida',
      subtitle: 'Esta negociação foi cancelada antes do início do disparo.',
      icon: FeatherBan,
      bgClass: 'bg-orange-50',
      iconBgClass: 'bg-orange-100',
      borderClass: 'border-orange-200',
      textClass: 'text-orange-700',
      badgeVariant: 'neutral',
    },
  };

  return tipo ? (configs[tipo] ?? FALLBACK_ENCERRAMENTO_CONFIG) : FALLBACK_ENCERRAMENTO_CONFIG;
};

const PROPOSTA_RECUSADA_CONFIG: EncerramentoConfig = {
  title: 'Proposta recusada',
  subtitle: 'A proposta não foi aceita. A negociação foi encerrada sem acordo.',
  icon: FeatherXCircle,
  bgClass: 'bg-error-50',
  iconBgClass: 'bg-error-100',
  borderClass: 'border-error-200',
  textClass: 'text-error-700',
  badgeVariant: 'error',
};

const RESULTADO_DIVERGENTE_CONFIG: EncerramentoConfig = {
  ...FALLBACK_ENCERRAMENTO_CONFIG,
  title: 'Resultado a confirmar',
  subtitle: 'Os dados do encerramento não coincidem. Consulte o histórico deste caso.',
  icon: FeatherAlertCircle,
};

const ARQUIVADA_CONFIG: EncerramentoConfig = {
  ...FALLBACK_ENCERRAMENTO_CONFIG,
  title: 'Negociação arquivada',
  subtitle: 'Este caso foi arquivado. Não há um resultado de encerramento identificado.',
};

const RESUMO_POR_TIPO: Record<TipoEncerramento, Pick<EncerramentoConfig, 'title' | 'subtitle'>> = {
  ACORDO_MANUAL: {
    title: 'Acordo fechado',
    subtitle: 'As partes chegaram a um acordo. Esta negociação foi encerrada.',
  },
  ACORDO_IA: {
    title: 'Acordo fechado',
    subtitle: 'As partes chegaram a um acordo. Esta negociação foi encerrada.',
  },
  DESISTENCIA: {
    title: 'Encerrada por desistência',
    subtitle: 'A contraparte desistiu antes de um acordo ser fechado.',
  },
  TIMEOUT: {
    title: 'Encerrada sem resposta',
    subtitle: 'A negociação terminou após o prazo sem resposta da contraparte.',
  },
  CANCELAMENTO: {
    title: 'Negociação cancelada',
    subtitle: 'O caso foi cancelado antes do início do disparo.',
  },
};

const TIPOS_SEM_ACORDO: ReadonlySet<string> = new Set(['DESISTENCIA', 'TIMEOUT', 'CANCELAMENTO']);

export const getResumoEncerramentoConfig = ({
  status,
  tipo,
  archivedAt,
}: {
  status: NegociacaoCasoStatus;
  tipo: string | null | undefined;
  archivedAt: string | null;
}): EncerramentoConfig => {
  const statusComAcordo = status === 'AGREEMENT_CLOSED';
  const statusSemAcordo = status === 'NO_AGREEMENT';
  const tipoComAcordo = tipo != null && TIPOS_ENCERRAMENTO_COM_ACORDO.has(tipo);
  const tipoSemAcordo = tipo != null && TIPOS_SEM_ACORDO.has(tipo);

  if (
    (statusComAcordo && (tipoSemAcordo || tipo === 'SEM_ACORDO')) ||
    (statusSemAcordo && (tipoComAcordo || tipo === 'ACORDO'))
  ) {
    return RESULTADO_DIVERGENTE_CONFIG;
  }

  if (statusComAcordo || (status === 'CLOSED' && tipoComAcordo)) {
    const acordoTipo = tipoComAcordo ? (tipo as TipoEncerramento) : 'ACORDO_MANUAL';
    return { ...getTipoEncerramentoConfig(acordoTipo), ...RESUMO_POR_TIPO[acordoTipo] };
  }

  if ((statusSemAcordo || status === 'CLOSED') && tipoSemAcordo) {
    const encerramentoTipo = tipo as TipoEncerramento;
    const config = getTipoEncerramentoConfig(encerramentoTipo);
    return {
      ...config,
      ...RESUMO_POR_TIPO[encerramentoTipo],
      ...(encerramentoTipo === 'CANCELAMENTO'
        ? {
            bgClass: 'bg-neutral-50',
            iconBgClass: 'bg-neutral-100',
            borderClass: 'border-neutral-200',
            textClass: 'text-neutral-700',
          }
        : {}),
    };
  }

  if (statusSemAcordo) return PROPOSTA_RECUSADA_CONFIG;
  if (archivedAt && status !== 'CLOSED') return ARQUIVADA_CONFIG;
  return {
    ...FALLBACK_ENCERRAMENTO_CONFIG,
    subtitle: 'Este caso está encerrado. O resultado não foi identificado.',
  };
};
